/**
 * ⚠️  INTERNAL USE ONLY — NOT FOR PUBLIC ACCESS
 * Route: /admin/gbp
 * Password-gated. noindex. Not linked from any public page.
 * Google Business Profile centralized management dashboard.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Star, MessageSquare, RefreshCw,
  CheckCircle2, AlertCircle, Clock, Wifi, WifiOff,
  ChevronDown, X, Save, Sparkles, Home, Lock,
  BarChart3, Building2, Filter, MapPin,
} from '../../lib/icons';
import { AdminLoginGate, isAdminAuthed } from '../../components/AdminLoginGate';
import SEOHead from '../../components/SEOHead';
import {
  getAccounts, getAuthUrl, syncAllAccounts, getReviews, syncReviews,
  saveDraft, generateAiReply, listAccessibleLocations, mapGoogleLocation,
  type GBPAccount, type GBPReview, type GBPLocationOption, type Tone, type ReviewFilters,
} from '../../lib/gbp-api';


// ── Constants ─────────────────────────────────────────────────────────────────
const TONES: { id: Tone; label: string; desc: string }[] = [
  { id: 'professional', label: 'Professional',    desc: 'Polished & confident' },
  { id: 'friendly',     label: 'Friendly',        desc: 'Warm & personal' },
  { id: 'premium',      label: 'Premium Service', desc: 'Expert-forward' },
  { id: 'casual',       label: 'Casual Local',    desc: 'Relaxed & genuine' },
];

const STAR_COLORS = ['', 'text-red-400', 'text-orange-400', 'text-yellow-400', 'text-lime-400', 'text-green-400'];
// ── Helpers ───────────────────────────────────────────────────────────────────
const relDate = (s: string) => {
  const diff = Date.now() - new Date(s).getTime();
  const d = Math.floor(diff / 86400000);
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 30)  return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
};

const StarRow = ({ n }: { n: number }) => (
  <span className="flex gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} className={`h-3.5 w-3.5 ${i <= n ? `${STAR_COLORS[n]} fill-current` : 'text-gray-700'}`} />
    ))}
  </span>
);

const isMapped = (acct: GBPAccount) =>
  acct.connected && Boolean(acct.google_location_name && acct.google_location_mapped_at);


// ── Account Card ──────────────────────────────────────────────────────────────
const AccountCard = ({
  acct, onConnect, onSync, onMapped, onError, connecting,
}: {
  acct: GBPAccount;
  onConnect: (key: string) => void;
  onSync: (key: string) => void;
  onMapped: () => void;
  onError: (message: string) => void;
  connecting: string | null;
}) => {
  const [mappingOpen, setMappingOpen] = useState(false);
  const [locations, setLocations] = useState<GBPLocationOption[]>([]);
  const [selected, setSelected] = useState('');
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [savingMapping, setSavingMapping] = useState(false);
  const mapped = isMapped(acct);

  const openMapping = async () => {
    setMappingOpen(true);
    setLoadingLocations(true);
    try {
      const opts = await listAccessibleLocations(acct.location_key);
      setLocations(opts);
      setSelected(acct.google_location_name || opts[0]?.location_name || '');
    } catch (e: unknown) {
      onError((e as Error).message);
    } finally {
      setLoadingLocations(false);
    }
  };

  const saveMapping = async () => {
    if (!selected) { onError('Select a Google Business Profile location first'); return; }
    setSavingMapping(true);
    try {
      await mapGoogleLocation(acct.location_key, selected);
      setMappingOpen(false);
      onMapped();
    } catch (e: unknown) {
      onError((e as Error).message);
    } finally {
      setSavingMapping(false);
    }
  };

  return (
  <div className={`rounded-xl border p-4 transition-all ${
    acct.connected ? 'bg-gray-900 border-gray-800' : 'bg-gray-900/50 border-gray-800/60'
  }`}>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[#E0E0E0] truncate">{acct.display_name}</p>
        <p className="text-[11px] text-gray-500 mt-0.5 truncate">
          {acct.google_email || acct.location_key}
        </p>
      </div>
      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${
        mapped
          ? 'bg-green-500/10 border-green-500/30 text-green-400'
          : acct.connected
            ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
            : 'bg-gray-700/30 border-gray-700 text-gray-500'
      }`}>
        {acct.connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
        {mapped ? 'Mapped' : acct.connected ? 'Unmapped' : 'Not connected'}
      </div>
    </div>

    {acct.connected && (
      <div className={`mb-3 rounded-lg border px-2 py-1.5 text-[11px] ${
        mapped ? 'border-green-500/20 bg-green-500/5 text-green-300' : 'border-yellow-500/20 bg-yellow-500/5 text-yellow-300'
      }`}>
        <div className="flex items-start gap-1.5">
          <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span className="min-w-0">
            {mapped
              ? `Mapped to ${acct.google_location_title || acct.google_location_name}`
              : 'Manual Google location selection required before review sync'}
          </span>
        </div>
      </div>
    )}

    {acct.connected && (
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-gray-800/60 rounded-lg p-2 text-center">
          <p className="text-base font-black text-[#E0E0E0]">{acct.totalReviews}</p>
          <p className="text-[10px] text-gray-500">Reviews</p>
        </div>
        <div className="bg-gray-800/60 rounded-lg p-2 text-center">
          <p className={`text-base font-black ${acct.unanswered > 0 ? 'text-yellow-400' : 'text-[#E0E0E0]'}`}>
            {acct.unanswered}
          </p>
          <p className="text-[10px] text-gray-500">Pending</p>
        </div>
        <div className="bg-gray-800/60 rounded-lg p-2 text-center">
          <p className="text-base font-black text-[#E0E0E0]">
            {acct.avgRating !== null ? acct.avgRating.toFixed(1) : '—'}
          </p>
          <p className="text-[10px] text-gray-500">Avg ★</p>
        </div>
      </div>
    )}

    {acct.connect_error && (
      <p className="text-[10px] text-red-400 bg-red-500/10 rounded-lg px-2 py-1 mb-2 truncate">
        {acct.connect_error}
      </p>
    )}

    <div className="flex gap-2">
      <button
        onClick={() => onConnect(acct.location_key)}
        disabled={connecting === acct.location_key}
        className="flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-all border-[#39FF14]/30 text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-50"
      >
        {connecting === acct.location_key ? 'Opening…' : acct.connected ? 'Reconnect' : 'Connect'}
      </button>
      {acct.connected && (
        <button
          onClick={() => onSync(acct.location_key)}
          disabled={!mapped}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          title={mapped ? 'Sync reviews' : 'Map this profile before syncing reviews'}
        >
          <RefreshCw className="h-3 w-3" />
        </button>
      )}
    </div>

    {acct.connected && (
      <button
        onClick={openMapping}
        className="mt-2 w-full py-1.5 rounded-lg text-xs font-semibold border border-[#00BFFF]/30 text-[#00BFFF] hover:bg-[#00BFFF]/10 transition-all"
      >
        {mapped ? 'Change Google Location' : 'Map Google Location'}
      </button>
    )}

    {mappingOpen && (
      <div className="mt-3 space-y-2 border-t border-gray-800 pt-3">
        {loadingLocations ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <RefreshCw className="h-3 w-3 animate-spin" /> Loading accessible locations
          </div>
        ) : locations.length === 0 ? (
          <p className="text-xs text-red-400">No accessible GBP locations found for this Google connection.</p>
        ) : (
          <>
            <select
              value={selected}
              onChange={e => setSelected(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-2 text-xs text-[#E0E0E0] focus:outline-none focus:border-[#00BFFF]"
            >
              {locations.map(loc => (
                <option key={loc.location_name} value={loc.location_name}>
                  {loc.title}{loc.locality ? ` — ${loc.locality}` : ''}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <button
                onClick={saveMapping}
                disabled={savingMapping}
                className="flex-1 py-1.5 rounded-lg bg-[#39FF14] text-[#0D0D0D] text-xs font-bold disabled:opacity-50"
              >
                {savingMapping ? 'Saving...' : 'Save Mapping'}
              </button>
              <button
                onClick={() => setMappingOpen(false)}
                className="px-2.5 py-1.5 rounded-lg border border-gray-700 text-gray-500 hover:text-gray-300"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </>
        )}
      </div>
    )}

    {acct.last_synced_at && (
      <p className="text-[10px] text-gray-600 mt-1.5">
        Synced {relDate(acct.last_synced_at)}
      </p>
    )}
  </div>
  );
};

// ── Review Card ───────────────────────────────────────────────────────────────
const ReviewCard = ({
  review, onSaveDraft, onAiGenerate,
  activeTone, onToneChange,
}: {
  review: GBPReview;
  onSaveDraft: (id: string, text: string) => Promise<void>;
  onAiGenerate: (review: GBPReview, tone: Tone) => Promise<string>;
  activeTone: Tone;
  onToneChange: (t: Tone) => void;
}) => {
  const [expanded, setExpanded]       = useState(false);
  const [replyText, setReplyText]     = useState(review.reply_draft || review.reply_text || '');
  const [aiLoading, setAiLoading]     = useState(false);
  const [draftSaved, setDraftSaved]   = useState(false);
  const [error, setError]             = useState('');

  const locationName = review.gbp_accounts?.display_name || '';
  const isReplied = review.reply_status === 'published';
  const isDraft   = review.reply_status === 'draft';
  const needsReply = !isReplied;
  const isLow = review.star_rating <= 2;

  const handleGenerate = async () => {
    setAiLoading(true); setError('');
    try {
      const text = await onAiGenerate(review, activeTone);
      setReplyText(text);
    } catch (e: unknown) { setError((e as Error).message); }
    finally { setAiLoading(false); }
  };

  const handleSaveDraft = async () => {
    setError('');
    try {
      await onSaveDraft(review.id, replyText);
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 2500);
    } catch (e: unknown) { setError((e as Error).message); }
  };

  return (
    <div className={`rounded-xl border transition-all ${
      isLow ? 'border-red-500/20 bg-red-500/5' : 'border-gray-800 bg-gray-900/50'
    }`}>
      {/* Header */}
      <div className="flex items-start gap-3 p-4">
        {/* Avatar */}
        <div className="w-9 h-9 rounded-full bg-gray-800 border border-gray-700 overflow-hidden shrink-0 flex items-center justify-center">
          {review.reviewer_photo_url
            ? <img src={review.reviewer_photo_url} alt="" className="w-full h-full object-cover" />
            : <span className="text-sm font-bold text-gray-400">
                {(review.reviewer_name || 'A')[0].toUpperCase()}
              </span>
          }
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-[#E0E0E0]">{review.reviewer_name}</span>
            <StarRow n={review.star_rating} />
            <span className="text-[10px] text-gray-500">{relDate(review.review_date)}</span>
          </div>
          <p className="text-[11px] text-gray-500 mt-0.5">{locationName}</p>

          {/* Badges */}
          <div className="flex gap-1.5 flex-wrap mt-1.5">
            {needsReply && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-400 border border-yellow-500/30 uppercase tracking-wide">
                Needs Response
              </span>
            )}
            {isDraft && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/30 uppercase tracking-wide">
                Draft Saved
              </span>
            )}
            {isLow && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30 uppercase tracking-wide">
                Low Rating
              </span>
            )}
            {isReplied && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-400 border border-gray-700 uppercase tracking-wide">
                Responded
              </span>
            )}
          </div>
        </div>

        <button
          onClick={() => setExpanded(e => !e)}
          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-gray-800 transition-colors shrink-0"
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Review text (always visible if present) */}
      {review.review_text && (
        <div className="px-4 pb-3">
          <p className="text-sm text-[#B0B0B0] leading-relaxed">{review.review_text}</p>
        </div>
      )}

      {/* Existing reply */}
      {isReplied && review.reply_text && !expanded && (
        <div className="px-4 pb-4">
          <div className="bg-gray-800/60 rounded-lg p-3 border-l-2 border-[#39FF14]/40">
            <p className="text-[10px] text-[#39FF14] font-bold mb-1">Your reply</p>
            <p className="text-xs text-gray-400">{review.reply_text}</p>
          </div>
        </div>
      )}

      {/* Reply editor (expanded) */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-800/60 pt-3">
          {/* Tone selector */}
          <div>
            <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide mb-1.5">Reply Tone</p>
            <div className="flex gap-1.5 flex-wrap">
              {TONES.map(t => (
                <button
                  key={t.id}
                  onClick={() => onToneChange(t.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
                    activeTone === t.id
                      ? 'bg-[#00BFFF]/15 border-[#00BFFF]/40 text-[#00BFFF]'
                      : 'border-gray-700 text-gray-500 hover:border-gray-600'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={aiLoading}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-[#39FF14]/30 text-[#39FF14] hover:bg-[#39FF14]/[0.06] transition-all text-xs font-bold disabled:opacity-50"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {aiLoading ? 'Generating…' : 'Generate AI Reply'}
          </button>

          {/* Text area */}
          <textarea
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            placeholder="Write your reply…"
            rows={4}
            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#39FF14] transition-colors resize-none placeholder:text-gray-600"
          />

          {error && (
            <p className="text-xs text-red-400 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleSaveDraft}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/[0.06] text-xs font-bold transition-all"
            >
              <Save className="h-3.5 w-3.5" />
              {draftSaved ? '✓ Draft Saved' : 'Save Draft'}
            </button>
            <div
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-700/40 text-gray-600 text-xs cursor-not-allowed select-none"
              title="Publish to Google — available in V2"
            >
              <Lock className="h-3 w-3" />
              V2
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Reviews Tab ───────────────────────────────────────────────────────────────
const ReviewsTab = ({ accounts }: { accounts: GBPAccount[] }) => {
  const [reviews, setReviews]       = useState<GBPReview[]>([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [syncing, setSyncing]       = useState<string | null>(null);
  const [syncError, setSyncError]   = useState('');
  const [activeTone, setActiveTone] = useState<Tone>('friendly');
  const [filters, setFilters]       = useState<ReviewFilters>({ sort: 'newest', per_page: 25 });
  const [page, setPage]             = useState(1);

  // Stable function — no filter/page in deps; all callers pass them explicitly.
  const loadReviews = useCallback(async (f: ReviewFilters, p: number) => {
    setLoading(true);
    try {
      const data = await getReviews({ ...f, page: p });
      setReviews(data.reviews);
      setTotal(data.total);
    } catch (e: unknown) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadReviews(filters, page); }, [loadReviews, filters, page]);

  const handleSync = async (lk: string) => {
    setSyncing(lk); setSyncError('');
    try {
      await syncReviews(lk);
      await loadReviews(filters, page);
    } catch (e: unknown) { setSyncError((e as Error).message); }
    finally { setSyncing(null); }
  };

  const setFilter = (k: keyof ReviewFilters, v: string | number | undefined) => {
    setPage(1);
    setFilters(prev => {
      const nf = { ...prev };
      if (v === undefined || v === '') delete (nf as Record<string, unknown>)[k];
      else (nf as Record<string, unknown>)[k] = v;
      return nf;
    });
  };

  // "Need Reply" uses the full-DB count from accounts (accurate even when paginating).
  const totalNeedReply = accounts.reduce((s, a) => s + a.unanswered, 0);
  const lowRating      = accounts.reduce((s, a) => s + a.lowStarReviews, 0);
  const mappedAccounts = accounts.filter(isMapped);
  const unmappedConnected = accounts.filter(a => a.connected && !isMapped(a));

  const starValue = filters.max_stars !== undefined ? 'max_3'
    : filters.min_stars !== undefined ? 'min_4' : '';

  return (
    <div className="space-y-4">
      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 text-center">
          <p className="text-2xl font-black text-[#E0E0E0]">{total}</p>
          <p className="text-xs text-gray-500 mt-0.5">Total Reviews</p>
        </div>
        <div className={`bg-gray-900 border rounded-xl p-3 text-center ${totalNeedReply > 0 ? 'border-yellow-500/30' : 'border-gray-800'}`}>
          <p className={`text-2xl font-black ${totalNeedReply > 0 ? 'text-yellow-400' : 'text-[#E0E0E0]'}`}>{totalNeedReply}</p>
          <p className="text-xs text-gray-500 mt-0.5">Need Reply</p>
        </div>
        <div className={`bg-gray-900 border rounded-xl p-3 text-center ${lowRating > 0 ? 'border-red-500/30' : 'border-gray-800'}`}>
          <p className={`text-2xl font-black ${lowRating > 0 ? 'text-red-400' : 'text-[#E0E0E0]'}`}>{lowRating}</p>
          <p className="text-xs text-gray-500 mt-0.5">Low Ratings</p>
        </div>
      </div>

      {/* Sync error */}
      {syncError && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="flex-1">{syncError}</span>
          <button onClick={() => setSyncError('')}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2">
          <Filter className="h-3.5 w-3.5 text-gray-500" />
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filters</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {/* Location */}
          <select
            value={filters.location_key || ''}
            onChange={e => setFilter('location_key', e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-[#E0E0E0] focus:outline-none"
          >
            <option value="">All Locations</option>
            {mappedAccounts.map(a => (
              <option key={a.location_key} value={a.location_key}>{a.display_name}</option>
            ))}
          </select>

          {/* Reply status */}
          <select
            value={filters.reply_status || ''}
            onChange={e => setFilter('reply_status', e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-[#E0E0E0] focus:outline-none"
          >
            <option value="">All Statuses</option>
            <option value="none">Needs Reply</option>
            <option value="draft">Draft Saved</option>
            <option value="published">Replied</option>
          </select>

          {/* Stars — always clears both min/max before setting to avoid stale clash */}
          <select
            value={starValue}
            onChange={e => {
              const v = e.target.value;
              setPage(1);
              setFilters(prev => {
                const nf = { ...prev };
                delete nf.min_stars;
                delete nf.max_stars;
                if (v === 'max_3') nf.max_stars = 3;
                if (v === 'min_4') nf.min_stars = 4;
                return nf;
              });
            }}
            className="bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-[#E0E0E0] focus:outline-none"
          >
            <option value="">All Stars</option>
            <option value="max_3">1–3 Stars</option>
            <option value="min_4">4–5 Stars</option>
          </select>

          {/* Sort */}
          <select
            value={filters.sort}
            onChange={e => setFilter('sort', e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-[#E0E0E0] focus:outline-none"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>

        {/* Sync buttons */}
        <div className="flex gap-2 mt-2 flex-wrap">
          {mappedAccounts.map(a => (
            <button
              key={a.location_key}
              onClick={() => handleSync(a.location_key)}
              disabled={syncing === a.location_key}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-700 text-[10px] text-gray-400 hover:border-gray-600 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${syncing === a.location_key ? 'animate-spin' : ''}`} />
              Sync {a.display_name.split(' – ')[1] || a.display_name}
            </button>
          ))}
        </div>

        {unmappedConnected.length > 0 && (
          <p className="text-[11px] text-yellow-400 mt-2">
            {unmappedConnected.length} connected profile{unmappedConnected.length === 1 ? '' : 's'} need manual Google location mapping before reviews can sync.
          </p>
        )}
      </div>

      {/* Review list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="h-6 w-6 text-gray-600 animate-spin" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 text-gray-600">
          <MessageSquare className="h-8 w-8 mx-auto mb-2" />
          <p className="text-sm">No reviews found</p>
          <p className="text-xs mt-1">Connect a Google account and sync to pull reviews</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map(r => (
            <ReviewCard
              key={r.id}
              review={r}
              activeTone={activeTone}
              onToneChange={setActiveTone}
              onSaveDraft={saveDraft}
              onAiGenerate={async (rev, tone) => generateAiReply({
                reviewer_name: rev.reviewer_name,
                star_rating:   rev.star_rating,
                review_text:   rev.review_text || undefined,
                tone,
                business_name: rev.gbp_accounts?.display_name || 'GameFix Solutions',
              })}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > (filters.per_page || 25) && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">{total} total reviews</p>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 disabled:opacity-30 hover:border-gray-600"
            >
              Prev
            </button>
            <button
              disabled={(page * (filters.per_page || 25)) >= total}
              onClick={() => setPage(p => p + 1)}
              className="px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 disabled:opacity-30 hover:border-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Overview Tab ──────────────────────────────────────────────────────────────
const OverviewTab = ({ accounts, onConnect }: { accounts: GBPAccount[]; onConnect: (lk: string) => void }) => {
  const [syncing, setSyncing] = useState(false);

  const connected  = accounts.filter(a => a.connected);
  const mapped     = accounts.filter(isMapped);
  const totalReviews = connected.reduce((s, a) => s + a.totalReviews, 0);
  const totalPending = connected.reduce((s, a) => s + a.unanswered, 0);
  const avgRatings   = connected.filter(a => a.avgRating).map(a => a.avgRating!);
  const overallAvg   = avgRatings.length
    ? Math.round(avgRatings.reduce((s, r) => s + r, 0) / avgRatings.length * 10) / 10
    : null;

  const syncAll = async () => {
    setSyncing(true);
    try { await syncAllAccounts(); }
    catch (e: unknown) { console.error(e); }
    finally { setSyncing(false); }
  };

  return (
    <div className="space-y-6">
      {/* Top stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Mapped Locations', val: `${mapped.length}/4`, icon: Building2, color: 'text-[#00BFFF]' },
          { label: 'Total Reviews', val: totalReviews, icon: MessageSquare, color: 'text-[#39FF14]' },
          { label: 'Need Reply', val: totalPending, icon: Clock, color: totalPending > 0 ? 'text-yellow-400' : 'text-gray-500' },
          { label: 'Avg Rating', val: overallAvg !== null ? `${overallAvg} ★` : '—', icon: Star, color: 'text-[#FF1744]' },
        ].map(stat => (
          <div key={stat.label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <stat.icon className={`h-5 w-5 ${stat.color} mb-2`} />
            <p className={`text-2xl font-black ${stat.color}`}>{stat.val}</p>
            <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Accounts overview */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#E0E0E0]">Connected Profiles</h3>
          <button
            onClick={syncAll}
            disabled={syncing || mapped.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:border-gray-600 transition-all disabled:opacity-40"
          >
            <RefreshCw className={`h-3 w-3 ${syncing ? 'animate-spin' : ''}`} />
            Sync All
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {accounts.map(a => (
            <div key={a.location_key} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-bold text-[#E0E0E0]">{a.display_name}</p>
                  <p className="text-[11px] text-gray-500">{a.google_email || 'Not connected'}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                  isMapped(a)
                    ? 'bg-green-500/10 border-green-500/30 text-green-400'
                    : a.connected
                      ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                    : 'bg-gray-700/30 border-gray-700 text-gray-500'
                }`}>
                  {isMapped(a) ? '● Mapped' : a.connected ? '● Unmapped' : '○ Disconnected'}
                </span>
              </div>
              {isMapped(a) && (
                <div className="flex gap-3">
                  <span className="text-xs text-gray-400">
                    <span className="font-bold text-[#E0E0E0]">{a.totalReviews}</span> reviews
                  </span>
                  <span className="text-xs text-gray-400">
                    <span className={`font-bold ${a.unanswered > 0 ? 'text-yellow-400' : 'text-[#E0E0E0]'}`}>{a.unanswered}</span> pending
                  </span>
                  {a.avgRating && (
                    <span className="text-xs text-gray-400">
                      <span className="font-bold text-[#E0E0E0]">{a.avgRating.toFixed(1)}</span> avg ★
                    </span>
                  )}
                </div>
              )}
              {a.connected && !isMapped(a) && (
                <p className="text-xs text-yellow-400 mt-2">Manual Google location mapping required before review sync.</p>
              )}
              {!a.connected && (
                <button
                  onClick={() => onConnect(a.location_key)}
                  className="mt-2 text-xs font-semibold text-[#39FF14] hover:underline"
                >
                  Connect →
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────
type Tab = 'overview' | 'reviews';

const GBPDashboard = () => {
  const [searchParams] = useSearchParams();
  const [accounts, setAccounts]       = useState<GBPAccount[]>([]);
  const [loadingAccts, setLoadingAccts] = useState(true);
  const [connecting, setConnecting]   = useState<string | null>(null);
  const [, setSyncingAcct] = useState<string | null>(null);
  const [activeTab, setActiveTab]     = useState<Tab>('overview');
  const [banner, setBanner]           = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const connected = searchParams.get('connected');
  const mappingRequired = searchParams.get('mapping_required');
  const oauthErr  = searchParams.get('error');

  // Show OAuth result banners
  useEffect(() => {
    if (connected) {
      setBanner({ type: 'success', msg: `Successfully connected ${connected.replace(/-/g, ' ')}` });
      loadAccounts();
    }
    if (mappingRequired) {
      setBanner({ type: 'success', msg: `Google connected for ${mappingRequired.replace(/-/g, ' ')}. Choose its Google Business Profile location before syncing reviews.` });
      loadAccounts();
    }
    if (oauthErr) {
      setBanner({ type: 'error', msg: `Connection failed: ${oauthErr}` });
    }
  }, [connected, mappingRequired, oauthErr]);

  const loadAccounts = async () => {
    setLoadingAccts(true);
    try { setAccounts(await getAccounts()); }
    catch (e: unknown) { console.error(e); }
    finally { setLoadingAccts(false); }
  };

  useEffect(() => { loadAccounts(); }, []);

  const handleConnect = async (lk: string) => {
    setConnecting(lk);
    try {
      const url = await getAuthUrl(lk);
      window.location.href = url;
    } catch (e: unknown) {
      setBanner({ type: 'error', msg: `Failed to start OAuth: ${(e as Error).message}` });
      setConnecting(null);
    }
  };

  const handleSync = async (lk: string) => {
    setSyncingAcct(lk);
    try {
      const n = await syncReviews(lk);
      setBanner({ type: 'success', msg: `Synced ${n} reviews from ${lk.replace(/-/g, ' ')}` });
      await loadAccounts();
    } catch (e: unknown) {
      setBanner({ type: 'error', msg: (e as Error).message });
    } finally { setSyncingAcct(null); }
  };

  const TABS: { id: Tab; label: string; icon: React.ElementType; badge?: number }[] = [
    { id: 'overview', label: 'Overview',  icon: BarChart3 },
    { id: 'reviews',  label: 'Reviews',   icon: MessageSquare,
      badge: accounts.reduce((s, a) => s + a.unanswered, 0) || undefined },
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-[#0D0D0D]/95 backdrop-blur border-b border-gray-800">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#FF1744]" />
              <span className="text-[#E0E0E0] font-black text-sm">GBP Dashboard</span>
            </div>
            <span className="text-[10px] bg-gray-800 text-gray-500 px-2 py-0.5 rounded font-semibold">INTERNAL</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/services" className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
              Services
            </Link>
            <span className="text-gray-700">·</span>
            <Link to="/admin/products" className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
              Products
            </Link>
            <span className="text-gray-700">·</span>
            <Link to="/" className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors">
              <Home className="h-3 w-3" /> Site
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">

        {/* Banner */}
        {banner && (
          <div className={`flex items-start gap-3 p-3 rounded-xl border mb-5 ${
            banner.type === 'success'
              ? 'bg-green-500/10 border-green-500/30 text-green-400'
              : 'bg-red-500/10 border-red-500/30 text-red-400'
          }`}>
            {banner.type === 'success'
              ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
              : <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            }
            <p className="text-sm flex-1">{banner.msg}</p>
            <button onClick={() => setBanner(null)} className="text-current opacity-60 hover:opacity-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Account connection bar */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-[#E0E0E0]">Google Business Profiles</h2>
            <span className="text-xs text-gray-500">
              {accounts.filter(isMapped).length}/{accounts.length} mapped
            </span>
          </div>
          {loadingAccts ? (
            <div className="flex justify-center py-6">
              <RefreshCw className="h-5 w-5 text-gray-600 animate-spin" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {accounts.map(a => (
                <AccountCard
                  key={a.location_key}
                  acct={a}
                  onConnect={handleConnect}
                  onSync={handleSync}
                  onMapped={async () => {
                    setBanner({ type: 'success', msg: `Saved Google location mapping for ${a.display_name}` });
                    await loadAccounts();
                  }}
                  onError={(msg) => setBanner({ type: 'error', msg })}
                  connecting={connecting}
                />
              ))}
            </div>
          )}
        </div>

        {/* Tab nav */}
        <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-xl p-1 mb-5">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all relative ${
                activeTab === t.id
                  ? 'bg-[#39FF14]/[0.12] text-[#39FF14]'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
              {t.badge !== undefined && t.badge > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-yellow-400 text-[#0D0D0D] text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center">
                  {t.badge > 9 ? '9+' : t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <OverviewTab accounts={accounts} onConnect={handleConnect} />
        )}
        {activeTab === 'reviews' && (
          <ReviewsTab accounts={accounts} />
        )}
      </div>
    </div>
  );
};

// ── Page entry point ──────────────────────────────────────────────────────────
const AdminGBPPage = () => {
  const [authed, setAuthed] = useState(isAdminAuthed());

  return (
    <>
      <SEOHead
        title="GBP Dashboard — Internal"
        description="Internal Google Business Profile management"
        noIndex
      />
      {authed ? <GBPDashboard /> : <AdminLoginGate onAuth={() => setAuthed(true)} />}
    </>
  );
};

export default AdminGBPPage;
